import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { prosthesisApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roles';
import { CommandeProtheseList, QueryParams, PaginatedResponse } from '../../types';
import {
  Package, Search, Plus, Eye, Edit, Calendar,
  User, Building, Trash2, AlertTriangle, ChevronLeft, ChevronRight, Filter,
} from 'lucide-react';

interface ProsthesisOrderListProps {
  onOrderSelect?: (orderId: number) => void;
  onEditOrder?: (orderId: number) => void;
  onCreateOrder?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  empreintes_prises:       { label: 'Empreintes prises',    color: '#6366f1', bg: '#eef2ff' },
  empreintes_envoyees:     { label: 'Empreintes envoyées',  color: '#8b5cf6', bg: '#f5f3ff' },
  en_fabrication:          { label: 'En fabrication',       color: '#0ea5e9', bg: '#f0f9ff' },
  fabrication_terminee:    { label: 'Fabrication terminée', color: '#0d9488', bg: '#f0fdfa' },
  prothese_livree_cabinet: { label: 'Livrée au cabinet',    color: '#10b981', bg: '#ecfdf5' },
  prothese_posee:          { label: 'Posée chez patient',   color: '#22c55e', bg: '#f0fdf4' },
  terminee:                { label: 'Terminée',             color: '#64748b', bg: '#f8fafc' },
  annulee:                 { label: 'Annulée',              color: '#ef4444', bg: '#fef2f2' },
};

const StatusBadge: React.FC<{ status: string; display?: string }> = ({ status, display }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: display ?? status, color: '#9ca3af', bg: '#f9fafb' };
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {display ?? cfg.label}
    </span>
  );
};

const PAGE_SIZE = 20;

const ProsthesisOrderList: React.FC<ProsthesisOrderListProps> = ({
  onOrderSelect, onEditOrder, onCreateOrder,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<CommandeProtheseList[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const isAdmin = hasRole(user, 'admin');

  const statusOptions = [
    { value: '', label: 'Tous les statuts' },
    ...Object.entries(STATUS_CONFIG).map(([v, cfg]) => ({ value: v, label: cfg.label })),
  ];

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params: QueryParams = {
        page: currentPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        ordering: '-created_at',
      };
      const response = await prosthesisApi.getCommandes(params);
      const data: PaginatedResponse<CommandeProtheseList> = response.data;
      setOrders(data.results);
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / PAGE_SIZE));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [currentPage, statusFilter]);
  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); fetchOrders(); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleDelete = async (order: CommandeProtheseList) => {
    if (!window.confirm(
      `Supprimer la commande ${order.numero_commande} ?\n\nPatient : ${order.patient_nom} ${order.patient_prenoms}\nCette action est irréversible.`
    )) return;
    setDeletingId(order.id);
    try {
      await prosthesisApi.deleteCommande(order.id);
      fetchOrders();
    } catch (error: any) {
      alert(error.response?.status === 403
        ? 'Permissions insuffisantes.'
        : `Erreur : ${error.response?.data?.detail ?? error.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const retardCount = orders.filter(o => o.is_en_retard).length;

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Commandes de prothèses</h1>
            <p className="text-xs text-gray-400">{totalCount} commande{totalCount > 1 ? 's' : ''} au total</p>
          </div>
        </div>
        {onCreateOrder && (
          <button
            onClick={onCreateOrder}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all shadow-sm hover:shadow"
            style={{ background: 'linear-gradient(135deg,#6366f1,#0ea5e9)' }}
          >
            <Plus className="w-4 h-4" />
            Nouvelle commande
          </button>
        )}
      </div>

      {/* ── Alerte retard ── */}
      {retardCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span><strong>{retardCount}</strong> commande{retardCount > 1 ? 's sont' : ' est'} en retard sur cette page.</span>
        </div>
      )}

      {/* ── Filtres ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              type="text"
              placeholder="Numéro, patient, prestataire…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm text-gray-700 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-colors"
            />
          </div>
          <div className="relative sm:w-56">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-4 py-2.5 text-sm text-gray-700 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 appearance-none bg-white transition-colors"
              aria-label="Filtrer par statut"
            >
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Tableau ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-56">
            <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <Package className="w-12 h-12 mb-3" />
            <p className="text-sm font-medium text-gray-400">Aucune commande trouvée</p>
            <p className="text-xs text-gray-300 mt-1">Modifiez vos critères de recherche</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Commande</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Patient</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden lg:table-cell">Prestataire</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden xl:table-cell">Type</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Statut</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden md:table-cell">Livraison</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Coût</th>
                  <th className="py-3 px-4 w-24" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map(order => (
                  <tr
                    key={order.id}
                    className={`hover:bg-gray-50/70 transition-colors ${order.is_en_retard ? 'bg-red-50/30' : ''}`}
                  >
                    <td className="py-3.5 px-5">
                      <p className="font-semibold text-gray-800">{order.numero_commande}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(order.date_empreintes).toLocaleDateString('fr-FR')}</p>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {order.patient_nom.charAt(0)}
                        </div>
                        <span
                          className="font-medium text-gray-700 cursor-pointer hover:text-indigo-600 hover:underline transition-colors"
                          onClick={() => navigate(`/patients/${order.patient_id ?? ''}`)}
                          title="Voir le dossier patient"
                        >
                          {order.patient_nom} {order.patient_prenoms}
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 hidden lg:table-cell">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Building className="w-3.5 h-3.5 text-gray-300" />
                        {order.prestataire_nom}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 hidden xl:table-cell">{order.type_prothese_nom}</td>
                    <td className="py-3.5 px-4">
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={order.status} display={order.status_display} />
                        {order.is_en_retard && (
                          <span className="inline-flex items-center gap-1 text-xs text-red-500 font-medium">
                            <AlertTriangle className="w-3 h-3" /> Retard
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 hidden md:table-cell">
                      <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                        <Calendar className="w-3.5 h-3.5 text-gray-300" />
                        {new Date(order.date_livraison_prevue).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-right hidden sm:table-cell">
                      <span className="font-semibold text-gray-700">
                        {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(order.cout_total)}
                        <span className="text-xs font-normal text-gray-400 ml-1">GNF</span>
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-end gap-1">
                        {onOrderSelect && (
                          <button onClick={() => onOrderSelect(order.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                            title="Voir détails">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {onEditOrder && (
                          <button onClick={() => onEditOrder(order.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
                            title="Modifier">
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => handleDelete(order)}
                            disabled={deletingId === order.id}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-40"
                            title="Supprimer">
                            {deletingId === order.id
                              ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                              : <Trash2 className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400 text-xs">
            {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} sur {totalCount}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="px-3 py-1.5 text-xs text-gray-600 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProsthesisOrderList;
